<template>
  <div>
    <BCard>
      <BRow class="mb-1">
        <BCol class="col-md-7">
          <h2 class="text-blue font-weight-bold col-md-10 col-lg-10 col-xl-11 mt-1">
            {{ $t("referral_program") }}
          </h2>
        </BCol>

        <BCol md="5" class="text-right">
          <BButton variant="primary" @click="$router.push('/referral-program/settings')">
            {{ $t("settings") }}
          </BButton>
        </BCol>
      </BRow>

      <BTable
          striped
          responsive
          hover
          :items="items"
          :fields="fields"
          :busy="loading"
          :current-page="currentPage"
          show-empty
      >
        <template #table-busy>
          <div class="text-center my-2">
            <CustomLoader :show="true"/>
          </div>
        </template>

        <template #cell(name)="data">
          <p class="badge badge-light-primary" style="max-width: 400px; white-space: normal; word-wrap: break-word; overflow-wrap: break-word;"
             @click="$router.push(`/referral-program/${data.item.id}`)" role="button">
            {{ getReferrerName(data.item) }}
          </p>
        </template>

        <template #cell(companies)="data">
            {{ companiesLabel(data.item) }}
        </template>

        <template #cell(code)="data">
          <p class="badge badge-light-primary" role="button" @click="copyLink(data.item)">
            {{ data.item.rrp_link }}
          </p>
        </template>

        <template #cell(collected_hive_fee)="data">
            {{ data.item.rrp_link }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.item.status)">
            {{ $t(data.item.status) }}
          </b-badge>
        </template>

        <template #cell(actions)="data">
          <div style="min-width: 180px">
            <BButton variant="outline-primary"
                      size="sm" class="mr-1"
                      v-b-tooltip.hover
                      :title="$t('details')"
                      @click="$router.push(`/referral-program/${data.item.id}`)"
            >
              <FeatherIcon icon="FileTextIcon"/>
            </BButton>

            <BButton v-if="adminType(['super_admin','admin_type_a']) && amountIsNotEmpty(data.item)"
                      size="sm"
                      class="mr-1"
                      v-b-tooltip.hover
                      :title="$t('confirm')"
                      variant="outline-success"
                      @click="openModal('confirm', data.item)"
            >
              <FeatherIcon icon="CheckIcon"/>
            </BButton>

            <BButton v-if="adminType(['super_admin']) && amountIsNotEmpty(data.item)"
                      size="sm"
                      class="mr-1"
                      v-b-tooltip.hover
                      :title="$t('cancel')"
                      variant="outline-danger"
                      @click="openModal('cancel', data.item)"
            >
              <FeatherIcon icon="XIcon"/>
            </BButton>
          </div>
        </template>
      </BTable>
    </BCard>
    <ApproveModal
        :text-content="modalText"
        :title="modalTitle"
        :action="modalAction"
        @ok="callApproveModalAction"
        @close="showModal = false"
    />
  </div>
</template>

<script>
import {BTable, BRow, BCol, BButton, BBadge} from "bootstrap-vue";
import CustomLoader from "@/components/Common/CustomLoader";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ApproveModal from '@/components/Common/ApproveModal'

export default {
  components: {
    BTable,
    BRow,
    BCol,
    BButton,
    BBadge,
    CustomLoader,
    ApproveModal,
  },
  data() {
    return {
      modalText: null,
      modalTitle: null,
      modalAction: null,
      modalItem: null,
      items: [],
      currentPage: 1,
      loading: false,
    };
  },

  created() {
    this.fetchData();
  },

  methods: {
    fetchData() {
      this.loading = true;
      this.$http.post(`/referral-program`)
          .then(res => {
            this.items = res?.data?.value?.data || [];
            this.currentPage = res?.data?.value?.current_page || 1;
          })
          .finally(() => {
            this.loading = false;
          });
    },
    statusVariant(status) {
      const map = {
        completed: "light-success",
        pending: "light-warning",
        no_earnings: "light-secondary",
      }

      return map[status] || "light-primary"
    },

    companiesLabel(item) {
      let companies = item?.companies;

      if (typeof companies === 'string') {
        try {
          companies = JSON.parse(companies);
        } catch {
          companies = [];
        }
      }

      if (!Array.isArray(companies) || !companies.length) {
        return '-';
      }

      return companies
        .map((company) => (company.company_name ? company.company_name : `C-${company.company_id}`))
        .join(', ');
    },

    copyLink(item) {
      navigator.clipboard.writeText(item.rrp_link)
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Copied!",
              icon: "CopyIcon",
              variant: "success",
            },
          });
        });
    },

    openModal(action, item) {
      this.modalAction = action
      this.modalItem = item
      this.modalTitle = action === 'confirm' ? this.$t('confirm') : this.$t('cancel')
      this.modalText = action === 'confirm' ? this.$t('are_you_sure_confirm_referral') : this.$t('are_you_sure_cancel_referral')
      this.$root.$bvModal.show('approveModal')
    },
    callApproveModalAction() {
      if (!this.modalItem?.id || !['confirm', 'cancel'].includes(this.modalAction)) {
        return;
      }
      this.submitReferralAction(this.modalAction, this.modalItem)
    },
    submitReferralAction(action, item) {
      const last = !this.items.some((referer) => referer.status === 'pending' && referer.id !== item.id);

      this.$http
          .post(`/referral-program/${item.id}/${action}`, { last })
          .then((res) => {
            if (res) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "success",
                  icon: "SuccessIcon",
                  variant: "success",
                },
              });
              this.fetchData()
            }
          });
    },
    amountIsNotEmpty(item) {
      return (
          item.collected_hive_fee_chf ||
          item.collected_hive_fee_eur ||
          item.collected_hive_fee_usd ||
          item.collected_hive_fee_gbp
      );
    },
    getReferrerName(item) {
      const company = item?.company;

      if (!company) {
        return 'Undefined';
      }

      if (company.name) {
        return company.name;
      }

      const firstName = company.main_contact_person?.name || '';
      const lastName = company.main_contact_person?.last_name || '';
      const fullName = `${firstName} ${lastName}`.trim();

      return fullName || 'Undefined';
    },
  },
  computed: {
    fields() {
      return [
        { key: "name", label: this.$t("referrer_name") },
        { key: "collected_hive_fee_chf", label: "CHF" },
        { key: "collected_hive_fee_eur", label: "EUR" },
        { key: "collected_hive_fee_usd", label: "USD" },
        { key: "collected_hive_fee_gbp", label: "GBP" },
        { key: "code", label: this.$t("code") },
        { key: "companies", label: this.$t("referred_companies") },
        { key: "status", label: this.$t("status") },
        { key: "actions", label: "actions", class: "text-right" },
      ]
    }
  }
};
</script>

<style scoped></style>
